// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface HeroLayout7Data {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: 'fill' | 'fit' | 'stretch';
  };
  imageUrl?: string;
  backgroundColor?: string;
  titleStyle?: any;
}

/**
 * Preview component for "Hero Layout 7" — renders a configurable hero/cover section with optional background image, title styling and a primary action button.
 * @component
 * @selector app-hero-layout-7-preview
 * @example
 *   <app-hero-layout-7-preview
 *     [data]="heroData"
 *     [headingColor]="'#1E3A8A'"
 *     [paragraphColor]="'#64748B'"
 *     [headingFont]="'Inter'"
 *     [bodyFont]="'Roboto'"
 *     [primaryColor]="'#3B82F6'"
 *     [titleStyles]="{ fontWeight: '700' }"
 *     (buttonClick)="onHeroButtonClick($event)">
 *   </app-hero-layout-7-preview>
 * @input {HeroLayout7Data | null} data - The main data model for the hero (may include image, title, subtitle, button text/link, etc.). Nullable.
 * @input {string | null} headingColor - Optional override color for the heading text.
 * @input {string | null} paragraphColor - Optional override color for paragraph/body text.
 * @input {string | null} headingFont - Optional font-family for the heading.
 * @input {string | null} bodyFont - Optional font-family for body/paragraph text.
 * @input {string | null} primaryColor - Optional primary/accent color used for buttons and highlights.
 * @input {any} titleStyles - Inline style overrides to apply to the title element (object of CSS properties).
 * @output {EventEmitter<{link: string; event: Event}>} buttonClick - Emitted when the hero's primary button is activated; payload includes the resolved link and the originating DOM event. Emission is guarded and only occurs if a link is provided.
 *
 * Key methods:
 * - onButtonClick(link: string | undefined, event: Event): void — Triggered by the template when the call-to-action is clicked; emits buttonClick with {link, event} if link is present.
 * - getBackgroundImage(): string — Returns a CSS background-image value derived from data.image.src or data.imageUrl, or an empty string if none is available.
 * - getDefaultBackgroundColor(): string — Returns the component's fallback gradient background CSS.
 * - getDefaultHeadingColor(): string — Returns the fallback heading color hex.
 * - getDefaultParagraphColor(): string — Returns the fallback paragraph color hex.
 * - getDefaultPrimaryColor(): string — Returns the fallback primary color hex.
 *
 * Important notes / lifecycle:
 * - This standalone component uses CommonModule and external template/style files.
 * - No Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are implemented explicitly in this class; configuration and rendering rely on the provided inputs and template bindings.
 */
@Component({
  selector: 'app-hero-layout-7-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hero-layout-7.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hero-layout-7.component.scss'],
})
export class HeroLayout7PreviewComponent {
  @Input() data: HeroLayout7Data | null = null;
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() titleStyles: any = {};
  @Output() buttonClick = new EventEmitter<{ link: string; event: Event }>();

  onButtonClick(link: string | undefined, event: Event): void {
    if (link) {
      this.buttonClick.emit({ link, event });
    }
  }

  getBackgroundImage(): string {
    const imageUrl = this.data?.image?.src || this.data?.imageUrl;
    if (imageUrl) {
      return `url(${imageUrl})`;
    }
    return '';
  }

  getDefaultBackgroundColor(): string {
    return 'linear-gradient(135deg, #C5D5EA 0%, #E5EBF4 50%, #D4E0F0 100%)';
  }

  getDefaultHeadingColor(): string {
    return '#1E3A8A';
  }

  getDefaultParagraphColor(): string {
    return '#64748B';
  }

  getDefaultPrimaryColor(): string {
    return '#3B82F6';
  }
}
